<script setup lang="tsx">
import { CirclePlus, Fold, Search } from "@element-plus/icons-vue";
import { ElButton } from "element-plus";
import { computed, onMounted, ref, watch } from "vue";
import { LoginAccountInSysOrgDto, SysOrgDto, SysOrgRoleName } from "~/api";
import UserViewer from "~/Compliance/components/UserViewer.vue";
import { apiClient } from "~/utils/apiClient";
import { emptyGuid } from "~/utils/constants";
import { refreshCaches } from "~/utils/miscUtils";

import { useRoute } from "vue-router";
import { useEntityDefinitionStore } from "~/stores/EntityDefinitionStore";
import { useMyInfoStore } from "~/stores/MyInfoStore";
import { localLoginAccount } from "~/types/MiscTypes";

const route = useRoute();
const whoToShow =
  (route.meta.whoToShow as SysOrgRoleName) ?? SysOrgRoleName.MEMBER;

const myInfoStore = useMyInfoStore();
const entityDefinitionStore = useEntityDefinitionStore();
const users = ref<localLoginAccount[]>([]);
const loading = ref(true);
const searchQuery = ref<string>("");
const systemGuid = "11111111-1111-1111-1111-111111111111";

const memberGuestInfo = {
  includeMembers: whoToShow === SysOrgRoleName.MEMBER,
  includeGuests: whoToShow === SysOrgRoleName.GUEST,
};

const hasUserAdminRole = myInfoStore.currentSysOrg.roles.includes(
  SysOrgRoleName.USER_ADMIN,
);

const filteredUsers = computed(() => {
  const searchQueryLowered = searchQuery.value.toLowerCase();
  const searchFiltered =
    searchQueryLowered !== "" && searchQueryLowered !== undefined;

  return users.value.filter((user: LoginAccountInSysOrgDto) => {
    if (searchFiltered) {
      const loginNameMatch = user.loginName
        ?.toLowerCase()
        .includes(searchQueryLowered);
      const emailMatch = user.email?.toLowerCase().includes(searchQueryLowered);

      if (!loginNameMatch && !emailMatch) {
        return false;
      }
    }

    const hasMemberRole = user.sysOrgRoles?.includes(SysOrgRoleName.MEMBER);
    const hasGuestRole = user.sysOrgRoles?.includes(SysOrgRoleName.GUEST);

    if (
      (hasMemberRole && !memberGuestInfo.includeMembers) ||
      (hasGuestRole && !memberGuestInfo.includeGuests)
    ) {
      return false;
    }
    if (user.userGuid === systemGuid) {
      return false;
    }

    return true;
  });
});
const orgList = ref<SysOrgDto[]>([]);

const prepareOrgs = async () => {
  orgList.value = myInfoStore.mySysOrgs ?? [];
};

const nameOfOrg = (guid: string) => {
  return orgList.value?.find((org) => org.guid === guid)?.name ?? "-";
};

const currentOrgGuid = ref(myInfoStore.currentSysOrgGuid);
watch(currentOrgGuid, () => {
  console.log("currentOrgGuid changed", currentOrgGuid.value);
  retrieveUsers();
  loading.value = false;
});

let retrievingUsers = false;
const retrieveUsers = async () => {
  if (retrievingUsers) {
    // may be called twice in a row
    return;
  }

  if (!currentOrgGuid.value) {
    return;
  }

  // user admin can see all users in the current org
  retrievingUsers = true;

  users.value = (
    await apiClient.loginAccount.getLoginAccounts(
      currentOrgGuid.value,
      whoToShow,
    )
  ).map(extendUser);
  retrievingUsers = false;
};

const showViewer = (row: localLoginAccount) => {
  selectedUser.value = row;
  hasChanges.value = false;
  drawerOpen.value = true;
  isAddingNewUser.value = false;
};

const reloadData = async () => {
  loading.value = true;
  // done in order... myInfoStore must be loaded before orgs and users
  await Promise.all([myInfoStore.load(), entityDefinitionStore.load()]);
  await prepareOrgs();
  await retrieveUsers();
  loading.value = false;
};

const createNewAccount = () => {
  selectedUser.value = {
    rowData: {
      userGuid: emptyGuid,
      isTempAccount: true,
      userOrgNames: [myInfoStore.currentSysOrg.name],
      currentOrgGuid: currentOrgGuid.value,
      defaultSysOrgGuid: myInfoStore.currentSysOrgGuid,
      sysOrgRoles: [SysOrgRoleName.MEMBER],
      sysOrgGuids: [currentOrgGuid.value],
    } as localLoginAccount,
  }.rowData;
  drawerOpen.value = true;
  hasChanges.value = false;
  isAddingNewUser.value = true;
};
//For the drawer
const drawerOpen = ref(false);
const selectedUser = ref<localLoginAccount | null>(null);
const isAddingNewUser = ref(false);
const hasChanges = ref(false);

const handleCloseViewer = () => {
  selectedUser.value = null;
  drawerOpen.value = false;
  if (hasChanges.value) {
    reloadData();
  }
};

const extendUser = (user: LoginAccountInSysOrgDto): localLoginAccount => {
  return {
    ...user,
    userOrgNames: user.sysOrgGuids
      ? user.sysOrgGuids.map((sysOrgGuid: string) => nameOfOrg(sysOrgGuid))
      : ["-"],
    currentOrgGuid: currentOrgGuid.value,
    sysOrgRoles: user.sysOrgRoles,
    entityName: user.entity?.name,
    entityTypeName: entityDefinitionStore.nameOfType(
      user.entity?.entityDefinitionGuid,
    ),
    entityTypeIcon: entityDefinitionStore.iconOfType(
      user.entity?.entityDefinitionGuid,
    ),
    housingOrgRoleNames: {},
  };
};

const handleUpdatedUser = async (user: LoginAccountInSysOrgDto) => {
  hasChanges.value = true;
  // may be added or updated
  const index = users.value.findIndex((d) => d.userGuid === user.userGuid);

  const extendedUser = extendUser(user);

  if (index !== undefined && index !== -1) {
    users.value.splice(index, 1, extendedUser);
  } else {
    users.value.push(extendedUser);
  }

  if (user.userGuid === myInfoStore.userInfo.userGuid) {
    await refreshCaches();
    location.reload();
  }
};

const allEmails = computed(() => {
  return users.value.map((u) => {
    return { userGuid: u.userGuid, email: u.email?.toLowerCase() };
  });
});

onMounted(async () => {
  await reloadData();
});
</script>

<template>
  <div class="Users">
    <div v-if="!loading" class="flexRow">
      <el-input
        v-model="searchQuery"
        clearable
        placeholder="Search"
        :prefix-icon="Search"
      />
      <span class="showingInTable">
        Showing {{ filteredUsers.length }} of
        {{ users.length }}
      </span>
      <RefreshButton @click="reloadData" />
      <el-button
        v-if="hasUserAdminRole"
        class="addButton"
        :icon="CirclePlus"
        title="Add new user account"
        width="100px"
        @click="createNewAccount"
      >
        New Login Account
      </el-button>
    </div>
  </div>

  <el-table
    v-loading="loading"
    :data="filteredUsers"
    class="UserTable"
    height="90vh"
    :default-sort="{ prop: 'loginName', order: 'ascending' }"
  >
    <el-table-column label="Login Name" width="300" sortable prop="loginName">
      <template #default="scope">
        <el-link
          v-if="hasUserAdminRole"
          :icon="Fold"
          underline="never"
          type="primary"
          @click="showViewer(scope.row)"
        >
          {{ scope.row.loginName }}
          <el-icon
            v-if="scope.row.currentOrgRoles?.includes(SysOrgRoleName.GUEST)"
            title="Guest"
            ><el-icon-user />
          </el-icon>
        </el-link>
        <span v-else>{{ scope.row.loginName }}</span>
      </template>
    </el-table-column>
    <el-table-column label="Associated Entity" width="300">
      <template #default="scope">
        <span v-if="scope.row.entity">
          <i :class="'mdi mdi-' + scope.row.entityTypeIcon" />

          {{ scope.row.entityName }}
          {{ ` (${scope.row.entityTypeName})` }}
        </span>
      </template>
    </el-table-column>
    <el-table-column label="Email" width="350">
      <template #default="scope">
        <div>
          {{ scope.row.email ? scope.row.email : "(No Email)" }}
        </div>
      </template>
    </el-table-column>
    <el-table-column label="Roles" width="300">
      <template #default="scope">
        <span>
          {{ scope.row.sysOrgRoles?.join(", ") }}
        </span>
      </template>
    </el-table-column>
    <el-table-column
      v-if="orgList.length > 1"
      label="Organizations"
      width="300"
    >
      <template #default="scope">
        <ul class="orgList">
          <li v-for="sysOrgGuid in scope.row.orgGuids" :key="sysOrgGuid">
            <span>{{ nameOfOrg(sysOrgGuid) }}</span>
          </li>
        </ul>
      </template>
    </el-table-column>
    <el-table-column label="Notes" width="300">
      <template #default="scope">
        <div>
          <el-tag v-if="scope.row.superAdmin" type="danger">SA</el-tag>
          <el-tag v-if="scope.row.isTempAccount" type="warning"
            >Temp Account</el-tag
          >
        </div>
      </template>
    </el-table-column>
  </el-table>
  <UserViewer
    v-if="selectedUser"
    :key="selectedUser?.userGuid"
    :user="selectedUser"
    :drawer-open="drawerOpen"
    :adding-new-user="isAddingNewUser"
    :existing-emails="allEmails"
    :who-to-show="whoToShow"
    :user-list="users"
    @drawer-closed="handleCloseViewer"
    @updated="handleUpdatedUser"
  />
</template>

<style lang="less">
.Users {
  display: grid;
  grid-template-rows: 60px auto;

  .loginRole {
    white-space: normal;
  }

  .icon {
    cursor: pointer;
    margin-left: 0.5em;
    color: var(--el-color-primary);
    font-size: 1.2em;
    vertical-align: text-bottom;
  }

  .ep-table .recently-changed {
    --ep-table-tr-bg-color: var(--ep-color-primary-light-7) !important;
  }
  .ep-table {
    // height: 75vh;
    --ep-table-border-color: #c1c2c3;
  }

  .filters {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 1em;
    margin: 1em;

    .alignRight {
      display: flex;
      align-items: center;
      gap: 1em;
    }
  }

  .flexRow {
    align-items: center;
    .refreshButton {
      margin: 0;
    }
  }

  .orgFilter {
    display: flex;
    align-items: center;
    gap: 5px;
    .el-select {
      width: 200px;
    }
  }
}
.UserTable {
  .orgList {
    padding: 0 0 0 2em;
    margin: 0;
  }
  .el-tag + .el-tag {
    margin-left: 5px;
  }
  .el-icon {
    margin-left: 10px;
  }
}
</style>
